<app-navbar></app-navbar>
<div class="profession-score-manager">

  <!-- Loading Overlay -->
  <div class="loading-overlay" *ngIf="isLoading">
    <div class="spinner"></div>
    <p>Loading data...</p>
  </div>

  <!-- Header -->
  <header class="header">
    <a *ngIf="isPrivateBenchmarkMode" class="private-back-link" routerLink="/recruitment/benchmarks" [queryParams]="{ orgId: privateOrganizationId }">← Back to private benchmarks</a>
    <h1>{{ isPrivateBenchmarkMode ? 'Private Benchmark Manager' : 'Profession Score Manager' }}</h1>
    <p class="subtitle">{{ isPrivateBenchmarkMode ? 'Choose a public benchmark as a template or create a new role, edit it, then save it only to this organization.' : 'Manage profession scores and subcategories' }}</p>
  </header>

  <section *ngIf="isPrivateBenchmarkMode" class="private-organization-card">
    <div class="private-organization-icon">🏢</div>
    <div>
      <span>Private benchmark workspace</span>
      <h2>{{ privateOrganizationName || 'Loading organization…' }}</h2>
      <code>{{ privateOrganizationId }}</code>
    </div>
  </section>

  <!-- Main Content -->
  <div class="content-container" *ngIf="!isLoading">

    <!-- Industry Filter Section -->
    <section class="industry-filter-section">
      <div class="section-title-row">
        <h2>{{ isPrivateBenchmarkMode ? 'Select a Public Benchmark Industry' : 'Select Industry' }}</h2>
        <div class="industry-actions">
          <button class="btn-add-industry" (click)="openNewIndustryDialog()">
            <span class="icon">+</span> New Industry
          </button>
          <button
            *ngIf="!isPrivateBenchmarkMode"
            class="btn-delete-industry"
            (click)="openDeleteIndustryDialog()"
            [disabled]="!selectedIndustry || filteredProfessions.length === 0"
            title="Delete selected industry and all its roles">
            🗑 Delete Industry
          </button>
        </div>
      </div>
      <div class="industry-buttons">
        <button
          *ngFor="let industry of industries"
          class="industry-btn"
          [class.active]="selectedIndustry === industry.name"
          (click)="selectIndustry(industry.name)">
          {{ industry.name }}
        </button>
      </div>
    </section>

    <!-- Two Column Layout -->
    <div class="two-column-layout">

      <!-- Left Column: Profession List -->
      <aside class="profession-list-panel">
        <div class="panel-header">
          <h3>{{ isPrivateBenchmarkMode ? 'Public Benchmark Templates' : 'List of Professions' }}</h3>
          <button
            class="btn-add-profession"
            (click)="openNewRoleTypeDialog()"
            [disabled]="!selectedIndustry"
            title="Add new role type">
            <span class="icon">+</span>
            {{ isPrivateBenchmarkMode ? 'New Private Role' : 'New Role Type' }}
          </button>
        </div>

        <div class="profession-list" *ngIf="selectedIndustry">
          <div
            *ngFor="let profession of filteredProfessions"
            class="profession-item"
            [class.selected]="selectedProfession?.roleType === profession.roleType"
            (click)="selectProfession(profession)">
            <div class="profession-item-content">
              <span class="role-type">{{ profession.roleType }}</span>
              <span class="subcategory-count">
                {{ getSubCategoryKeys(profession).length }} subcategories
              </span>
            </div>
            <button
              *ngIf="!isPrivateBenchmarkMode"
              class="btn-delete-role"
              (click)="openDeleteRoleDialog(profession); $event.stopPropagation()"
              title="Delete this role">
              🗑
            </button>
          </div>

          <div class="empty-state" *ngIf="filteredProfessions.length === 0">
            <p>No professions found for this industry</p>
          </div>
        </div>

        <div class="empty-state" *ngIf="!selectedIndustry">
          <p>Select an industry to view professions</p>
        </div>
      </aside>

      <!-- Right Column: Score Editor -->
      <main class="score-editor-panel">
        <div *ngIf="!selectedProfession" class="empty-state-large">
          <div class="empty-icon">📊</div>
          <h3>No Profession Selected</h3>
          <p>Select a profession from the list to view and edit scores</p>
        </div>

        <div *ngIf="selectedProfession && editedProfession" class="editor-content">

          <!-- Editor Header -->
          <div class="editor-header">
            <div class="editor-title">
              <h2>{{ editedProfession.roleType }}</h2>
              <span class="industry-badge">{{ editedProfession.industry }}</span>
            </div>

            <div class="editor-actions">
              <button
                class="btn-toggle-behavioral"
                (click)="toggleBehavioralScoring()"
                [disabled]="isFlipping"
                title="Toggle Behavioral Scoring">
                <span class="icon">🔄</span>
                {{ showBehavioralScoring ? 'Cognitive Scoring' : 'Behavioral Scoring' }}
              </button>

              <button
                class="btn-secondary"
                (click)="cancelEditing()"
                [disabled]="!hasUnsavedChanges() || isSaving">
                Cancel
              </button>
              <button
                class="btn-primary"
                (click)="updateProfession()"
                [disabled]="isPrivateBenchmarkMode ? !canSaveProfession() : (!hasUnsavedChanges() || isSaving)">
                <span *ngIf="!isSaving">{{ isPrivateBenchmarkMode ? (privateBenchmarkPersisted ? 'Update Private Benchmark' : 'Save Private Benchmark') : 'Update Profession' }}</span>
                <span *ngIf="isSaving">Saving...</span>
              </button>
            </div>
          </div>

          <!-- Flip Card Container -->
          <div class="flip-card-container" [class.flipped]="showBehavioralScoring">
            <div class="flip-card-inner">

              <!-- FRONT: Cognitive Scoring -->
              <div class="flip-card-front">

                <!-- Add Subcategory Section -->
                <div class="add-subcategory-section">
                  <button
                    class="btn-add-subcategory"
                    (click)="toggleSubCategoryDropdown()">
                    <span class="icon">+</span>
                    Add Subcategory
                  </button>

                  <!-- Subcategory Dropdown -->
                  <div class="subcategory-dropdown" *ngIf="showSubCategoryDropdown">
                    <div class="dropdown-header">
                      <input
                        type="text"
                        class="search-input"
                        placeholder="Search subcategories..."
                        [formControl]="searchControl"
                        (click)="$event.stopPropagation()">
                      <button class="btn-close" (click)="toggleSubCategoryDropdown()">×</button>
                    </div>
                    <div class="dropdown-list">
                      <div
                        *ngFor="let subCategory of filteredSubCategories"
                        class="dropdown-item"
                        (click)="addSubCategory(subCategory)">
                        <div class="subcategory-info">
                          <span class="subcategory-name">{{ subCategory.subType || subCategory.subTypeId }}</span>
                          <span class="subcategory-id">{{ subCategory.subTypeId }}</span>
                        </div>
                        <span class="subcategory-type">{{ subCategory.typeId }}</span>
                      </div>
                      <div class="empty-state" *ngIf="filteredSubCategories.length === 0">
                        <p>No subcategories found</p>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Score Tables -->
                <div class="score-tables">

                  <!-- Distribution Scores -->
                  <div class="score-table-container">
                    <h3 class="table-title">
                      <span class="title-icon">📊</span>
                      Distribution Scores
                    </h3>
                    <div class="score-table">
                      <div class="table-header">
                        <div class="col-subcategory">Subcategory</div>
                        <div class="col-value">Value</div>
                        <div class="col-actions">Actions</div>
                      </div>
                      <div
                        *ngFor="let key of getSubCategoryKeys(editedProfession)"
                        class="table-row">
                        <div class="col-subcategory">
                          <span class="subcategory-label">{{ getSubCategoryDisplayName(key) }}</span>
                          <span class="subcategory-id-small">{{ key }}</span>
                        </div>
                        <div class="col-value">
                          <input
                            type="text"
                            class="value-input"
                            [value]="formatValue(editedProfession.distribution[key] ?? '')"
                            (input)="updateDistributionValue(key, $any($event.target).value)"
                            placeholder="Enter value">
                        </div>
                        <div class="col-actions">
                          <button class="btn-remove" (click)="removeSubCategory(key)" title="Remove subcategory">
                            <span class="icon">×</span>
                          </button>
                        </div>
                      </div>
                      <div class="empty-state" *ngIf="getSubCategoryKeys(editedProfession).length === 0">
                        <p>No subcategories added. Click "Add Subcategory" to get started.</p>
                      </div>
                    </div>
                  </div>

                  <!-- Raw Scores -->
                  <div class="score-table-container">
                    <h3 class="table-title">
                      <span class="title-icon">🎯</span>
                      Raw Scores
                    </h3>
                    <div class="score-table">
                      <div class="table-header">
                        <div class="col-subcategory">Subcategory</div>
                        <div class="col-value">Value</div>
                        <div class="col-actions">Actions</div>
                      </div>
                      <div
                        *ngFor="let key of getSubCategoryKeys(editedProfession)"
                        class="table-row">
                        <div class="col-subcategory">
                          <span class="subcategory-label">{{ getSubCategoryDisplayName(key) }}</span>
                          <span class="subcategory-id-small">{{ key }}</span>
                        </div>
                        <div class="col-value">
                          <input
                            type="text"
                            class="value-input"
                            [value]="formatValue(editedProfession['raw-score'][key] ?? '')"
                            (input)="updateRawScoreValue(key, $any($event.target).value)"
                            placeholder="Enter value">
                        </div>
                        <div class="col-actions">
                          <button class="btn-remove" (click)="removeSubCategory(key)" title="Remove subcategory">
                            <span class="icon">×</span>
                          </button>
                        </div>
                      </div>
                      <div class="empty-state" *ngIf="getSubCategoryKeys(editedProfession).length === 0">
                        <p>No subcategories added. Click "Add Subcategory" to get started.</p>
                      </div>
                    </div>
                  </div>

                </div>
              </div>
              <!-- END FRONT -->

              <!-- BACK: Behavioral Scoring -->
              <div class="flip-card-back">
                <div class="behavioral-scoring-content">

                  <!-- Overall Cutoff -->
                  <div class="behavioral-header">
                    <h3>Behavioral Scoring Configuration</h3>
                     <label class="behavioral-enable-toggle">
                      <input
                        type="checkbox"
                        [checked]="editedProfession.behavioral?.evaluate === 'True'"
                        (change)="setBehavioralEnabled($any($event.target).checked)">
                      Enable behavioral scoring for this role
                    </label>
                    <div class="overall-cutoff-section">
                      <label>Overall Cutoff:</label>
                      <input
                        type="number"
                        class="cutoff-input"
                        [value]="editedProfession.behavioral['overall-cutoff']"
                        (input)="updateOverallCutoff($any($event.target).value)"
                        min="0"
                        max="100">
                    </div>
                  </div>

                  <!-- Add Behavior Option Section -->
                  <div class="add-behavior-section">
                    <button
                      class="btn-add-subcategory"
                      (click)="toggleBehaviorOptionDropdown()">
                      <span class="icon">+</span>
                      Add Behavior Option
                    </button>

                    <button
                      class="btn-add-all"
                      (click)="addAllBehaviorOptions()">
                      <span class="icon">📋</span>
                      Add All Options
                    </button>

                    <!-- Behavior Option Dropdown -->
                    <div class="subcategory-dropdown" *ngIf="showBehaviorOptionDropdown">
                      <div class="dropdown-header">
                        <input
                          type="text"
                          class="search-input"
                          placeholder="Search behavior options..."
                          [formControl]="searchBehaviorControl"
                          (click)="$event.stopPropagation()">
                        <button class="btn-close" (click)="toggleBehaviorOptionDropdown()">×</button>
                      </div>
                      <div class="dropdown-list">
                        <div
                          *ngFor="let option of filteredBehaviorOptions"
                          class="dropdown-item"
                          (click)="addBehaviorOption(option)">
                          <div class="subcategory-info">
                            <span class="subcategory-name">{{ option.categoryName }}</span>
                            <span class="subcategory-id" *ngIf="option.categoryDescription">
                              {{ option.categoryDescription }}
                            </span>
                          </div>
                          <span class="subcategory-type">{{ option.subcategoryId }}</span>
                        </div>
                        <div class="empty-state" *ngIf="filteredBehaviorOptions.length === 0">
                          <p>No behavior options found</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Scrollable Container for Categories -->
                  <div class="behavioral-scroll-container">

                    <!-- Behavior Subcategories List (Grouped) -->
                    <div class="behavior-categories-container">
                      <div *ngFor="let subcategoryId of getSubcategoryIds()" class="behavior-category-card">

                        <!-- Subcategory Header -->
                        <div class="category-card-header">
                          <h4>{{ getSubcategoryDisplayName(subcategoryId) }}</h4>
                          <div class="category-actions">
                            <button
                              class="btn-remove-small"
                              (click)="removeSubcategoryGroup(subcategoryId)"
                              title="Remove all options in this subcategory">
                              ×
                            </button>
                          </div>
                        </div>

                        <!-- Subcategory Weight -->
                        <div class="category-weight-row">
                          <label>Subcategory Weight:</label>
                          <input
                            type="number"
                            class="weight-input"
                            [value]="editedProfession.behavioral.weights[subcategoryId]"
                            (input)="updateSubcategoryWeight(subcategoryId, $any($event.target).value)"
                            min="0"
                            max="1"
                            step="0.1"
                            placeholder="0.0 - 1.0">
                        </div>

                        <!-- Options Table -->
                        <div class="options-table">
                          <div class="options-header">
                            <div class="col-option-name">Option</div>
                            <div class="col-description">Description</div>
                            <div class="col-score">Score</div>
                            <div class="col-actions-small">Actions</div>
                          </div>
                          <div
                            *ngFor="let option of getBehaviorOptionsForSubcategory(subcategoryId)"
                            class="option-row">
                            <div class="col-option-name">
                              <span class="option-label">{{ option.categoryName }}</span>
                            </div>
                            <div class="col-description">
                              <span
                                class="option-desc"
                                [title]="option.categoryDescription || ''"
                                [class.has-tooltip]="option.categoryDescription && option.categoryDescription.length > 80">
                                {{ option.categoryDescription
                                    ? (option.categoryDescription.length > 80
                                        ? (option.categoryDescription | slice:0:80) + '...'
                                        : option.categoryDescription)
                                    : '-' }}
                              </span>
                            </div>
                            <div class="col-score">
                              <input
                                type="number"
                                class="score-input"
                                [value]="editedProfession.behavioral['personality-contribution'][subcategoryId][option.categoryId]"
                                (input)="updateBehaviorOptionScore(subcategoryId, option.categoryId, $any($event.target).value)"
                                min="0"
                                max="100"
                                placeholder="0-100">
                            </div>
                            <div class="col-actions-small">
                              <button
                                class="btn-remove-small"
                                (click)="removeBehaviorOption(subcategoryId, option.categoryId)"
                                title="Remove option">
                                ×
                              </button>
                            </div>
                          </div>
                          <div class="empty-state-small" *ngIf="getBehaviorOptionsForSubcategory(subcategoryId).length === 0">
                            <p>No options in this subcategory.</p>
                          </div>
                        </div>

                      </div>

                      <div class="empty-state" *ngIf="getSubcategoryIds().length === 0">
                        <p>No behavior options added. Click "Add Behavior Option" to get started.</p>
                      </div>
                    </div>

                    <!-- Cross Mapping Section -->
                    <div class="cross-mapping-section">
                      <div class="section-header">
                        <h3>Cross-Subcategory Mapping Weights</h3>
                        <button class="btn-add-cross" (click)="openCrossWeightDialog()">
                          <span class="icon">+</span>
                          Add Cross Mapping
                        </button>
                      </div>
                      <div class="cross-mappings-list">
                        <div *ngFor="let crossKey of getCrossMappings()" class="cross-mapping-item">
                          <span class="cross-key">{{ crossKey }}</span>
                          <input
                            type="number"
                            class="cross-weight-input"
                            [value]="editedProfession.behavioral['crossmapping-weights'][crossKey]"
                            (input)="updateCrossWeight(crossKey, $any($event.target).value)"
                            min="0"
                            max="1"
                            step="0.05"
                            placeholder="0.0 - 1.0">
                          <button
                            class="btn-remove-small"
                            (click)="removeCrossMapping(crossKey)"
                            title="Remove cross mapping">
                            ×
                          </button>
                        </div>
                        <div class="empty-state-small" *ngIf="getCrossMappings().length === 0">
                          <p>No cross mappings added.</p>
                        </div>
                      </div>
                    </div>

                  </div>
                  <!-- END behavioral-scroll-container -->

                </div>
              </div>
              <!-- END BACK -->

            </div>
          </div>
          <!-- END flip-card-container -->

          <!-- Unsaved Changes Warning -->
          <div class="unsaved-changes-warning" *ngIf="hasUnsavedChanges() || (isPrivateBenchmarkMode && !privateBenchmarkPersisted)">
            <span class="warning-icon">⚠️</span>
            {{ isPrivateBenchmarkMode ? 'This private benchmark has not been saved for the organization.' : 'You have unsaved changes. Click "Update Profession" to save.' }}
          </div>

        </div>
      </main>

    </div>
    <!-- END two-column-layout -->

  </div>
  <!-- END content-container -->


  <!-- ─────────────────────────────────────────────
       MODALS
  ───────────────────────────────────────────── -->

  <!-- New Role Type Dialog -->
  <div class="modal-overlay" *ngIf="showNewRoleTypeDialog" (click)="showNewRoleTypeDialog = false">
    <div class="modal" (click)="$event.stopPropagation()">
      <div class="modal-header">
        <h3>Create New Role Type</h3>
        <button class="btn-close" (click)="showNewRoleTypeDialog = false">×</button>
      </div>
      <div class="modal-body">
        <p class="modal-description">
          Create a new role type in the <strong>{{ selectedIndustry }}</strong> industry.
        </p>
        <div class="form-group">
          <label for="newRoleTypeName">Role Type Name</label>
          <input
            id="newRoleTypeName"
            type="text"
            class="form-input"
            [(ngModel)]="newRoleTypeName"
            placeholder="e.g., corporate-finance"
            (keyup.enter)="createNewRoleType()">
        </div>
      </div>
      <div class="modal-footer">
        <button class="btn-secondary" (click)="showNewRoleTypeDialog = false" [disabled]="isSaving">
          Cancel
        </button>
        <button
          class="btn-primary"
          (click)="createNewRoleType()"
          [disabled]="!newRoleTypeName.trim() || isSaving">
          <span *ngIf="!isSaving">Create</span>
          <span *ngIf="isSaving">Creating...</span>
        </button>
      </div>
    </div>
  </div>

  <!-- New Industry Dialog -->
  <div class="modal-overlay" *ngIf="showNewIndustryDialog" (click)="showNewIndustryDialog = false">
    <div class="modal" (click)="$event.stopPropagation()">
      <div class="modal-header">
        <h3>Create New Industry</h3>
        <button class="btn-close" (click)="showNewIndustryDialog = false">×</button>
      </div>
      <div class="modal-body">
        <p class="modal-description">
          Enter a name for the new industry. You'll be able to add roles to it straight after.
        </p>
        <div class="form-group">
          <label>Industry Name</label>
          <input
            type="text"
            class="form-input"
            [(ngModel)]="newIndustryName"
            placeholder="e.g., Healthcare"
            (keyup.enter)="createNewIndustry()">
        </div>
      </div>
      <div class="modal-footer">
        <button class="btn-secondary" (click)="showNewIndustryDialog = false">Cancel</button>
        <button
          class="btn-primary"
          (click)="createNewIndustry()"
          [disabled]="!newIndustryName.trim()">
          Create Industry
        </button>
      </div>
    </div>
  </div>

  <!-- Delete Industry Confirmation -->
  <div class="modal-overlay" *ngIf="showDeleteIndustryDialog" (click)="showDeleteIndustryDialog = false">
    <div class="modal" (click)="$event.stopPropagation()">
      <div class="modal-header">
        <h3>Delete Industry</h3>
        <button class="btn-close" (click)="showDeleteIndustryDialog = false">×</button>
      </div>
      <div class="modal-body">
        <p class="modal-description">
          Are you sure you want to delete the <strong>{{ industryToDelete }}</strong> industry?
          This will permanently delete <strong>all {{ filteredProfessions.length }} roles</strong> within it.
          This action cannot be undone.
        </p>
      </div>
      <div class="modal-footer">
        <button class="btn-secondary" (click)="showDeleteIndustryDialog = false" [disabled]="isSaving">
          Cancel
        </button>
        <button class="btn-danger" (click)="confirmDeleteIndustry()" [disabled]="isSaving">
          <span *ngIf="!isSaving">Delete Industry</span>
          <span *ngIf="isSaving">Deleting...</span>
        </button>
      </div>
    </div>
  </div>

  <!-- Delete Role Confirmation -->
  <div class="modal-overlay" *ngIf="showDeleteRoleDialog" (click)="showDeleteRoleDialog = false">
    <div class="modal" (click)="$event.stopPropagation()">
      <div class="modal-header">
        <h3>Delete Role</h3>
        <button class="btn-close" (click)="showDeleteRoleDialog = false">×</button>
      </div>
      <div class="modal-body">
        <p class="modal-description">
          Are you sure you want to delete the role <strong>{{ roleToDelete?.roleType }}</strong>?
          This action cannot be undone.
        </p>
      </div>
      <div class="modal-footer">
        <button class="btn-secondary" (click)="showDeleteRoleDialog = false" [disabled]="isSaving">
          Cancel
        </button>
        <button class="btn-danger" (click)="confirmDeleteRole()" [disabled]="isSaving">
          <span *ngIf="!isSaving">Delete Role</span>
          <span *ngIf="isSaving">Deleting...</span>
        </button>
      </div>
    </div>
  </div>

  <!-- Cross Weight Dialog -->
  <div class="modal-overlay" *ngIf="showCrossWeightDialog" (click)="showCrossWeightDialog = false">
    <div class="modal" (click)="$event.stopPropagation()">
      <div class="modal-header">
        <h3>Add Cross-Subcategory Mapping</h3>
        <button class="btn-close" (click)="showCrossWeightDialog = false">×</button>
      </div>
      <div class="modal-body">
        <p class="modal-description">
          Define a cross-mapping weight between two behavior subcategories.
        </p>
        <div class="form-group">
          <label for="crossSubcategory1">First Subcategory</label>
          <select id="crossSubcategory1" class="form-input" [(ngModel)]="selectedCrossSubcategory1">
            <option value="">Select subcategory...</option>
            <option *ngFor="let subcatId of getSubcategoryIds()" [value]="subcatId">
              {{ getSubcategoryDisplayName(subcatId) }}
            </option>
          </select>
        </div>
        <div class="form-group">
          <label for="crossSubcategory2">Second Subcategory</label>
          <select id="crossSubcategory2" class="form-input" [(ngModel)]="selectedCrossSubcategory2">
            <option value="">Select subcategory...</option>
            <option *ngFor="let subcatId of getSubcategoryIds()" [value]="subcatId">
              {{ getSubcategoryDisplayName(subcatId) }}
            </option>
          </select>
        </div>
        <div class="form-group">
          <label for="crossWeight">Cross Weight (0.0 - 1.0)</label>
          <input
            id="crossWeight"
            type="number"
            class="form-input"
            [(ngModel)]="crossWeightValue"
            min="0"
            max="1"
            step="0.05"
            placeholder="e.g., 0.25">
        </div>
      </div>
      <div class="modal-footer">
        <button class="btn-secondary" (click)="showCrossWeightDialog = false">Cancel</button>
        <button class="btn-primary" (click)="addCrossWeight()">Add Cross Mapping</button>
      </div>
    </div>
  </div>
   
  <!-- empty udpate confirmations dialog -->
  <div class="modal-overlay" *ngIf="showEmptyUpdateConfirmDialog" (click)="cancelEmptyUpdate()">
  <div class="modal" (click)="$event.stopPropagation()">
    <div class="modal-header">
      <h3>⚠️ This will clear existing data</h3>
      <button class="btn-close" (click)="cancelEmptyUpdate()">×</button>
    </div>
    <div class="modal-body">
      <p class="modal-description">
        Saving now will clear the following field(s), which currently have data
        for <strong>{{ editedProfession?.roleType }}</strong>:
      </p>
      <ul>
        <li *ngFor="let field of wipeWarningFields"><strong>{{ field }}</strong></li>
      </ul>
      <p class="modal-description">
        Are you sure you want to continue? This cannot be undone.
      </p>
    </div>
    <div class="modal-footer">
      <button class="btn-secondary" (click)="cancelEmptyUpdate()" [disabled]="isSaving">
        Cancel
      </button>
      <button class="btn-danger" (click)="confirmEmptyUpdate()" [disabled]="isSaving">
        <span *ngIf="!isSaving">Yes, clear it and save</span>
        <span *ngIf="isSaving">Saving...</span>
      </button>
    </div>
  </div>
</div>
</div>
<!-- END profession-score-manager -->

<app-footer></app-footer>
